import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowDownIcon from './assets/faq/ArrowDown.svg'
import ExpandPlusIcon from './assets/faq/ExpandPlusIcon.svg'
import UnexpandedIcon from './assets/faq/UnexpandedIcon.svg'
import { useState } from 'react'
import { useTranslate } from '@repo/unicity'

type FAQItem = {
    questionKey: string
    answerKeys: string[]
    htmlAnswers?: boolean
}

type FAQSection = {
    titleKey: string
    items: FAQItem[]
}

/**
 * FAQ Items using dictionary keys
 * Question keys: glic2027_q_{key_name}
 * Answer keys: glic2027_a_{key_name} or glic2027_a_{key_name}_1, glic2027_a_{key_name}_2 for multiple answers
 */
const FAQItems: FAQSection[] = [
    {
        titleKey: 'glic2027_section_ticket_purchase',
        items: [
            {
                questionKey: 'glic2027_q_purchase_onsite',
                answerKeys: ['glic2027_a_purchase_onsite_1', 'glic2027_a_purchase_onsite_2']
            },
            {
                questionKey: 'glic2027_q_age_restriction',
                answerKeys: ['glic2027_a_age_restriction_1', 'glic2027_a_age_restriction_2']
            },
            {
                questionKey: 'glic2027_q_children_price',
                answerKeys: ['glic2027_a_children_price']
            },
            {
                questionKey: 'glic2027_q_choose_seats',
                answerKeys: ['glic2027_a_choose_seats']
            },
            {
                questionKey: 'glic2027_q_wrong_seating_zone',
                answerKeys: ['glic2027_a_wrong_seating_zone']
            },
            {
                questionKey: 'glic2027_q_korean_thailand_office',
                answerKeys: ['glic2027_a_korean_thailand_office']
            },
            {
                questionKey: 'glic2027_q_no_confirmation',
                answerKeys: ['glic2027_a_no_confirmation']
            },
            {
                questionKey: 'glic2027_q_bulk_purchase',
                answerKeys: ['glic2027_a_bulk_purchase']
            },
            {
                questionKey: 'glic2027_q_receive_tickets',
                answerKeys: ['glic2027_a_receive_tickets']
            },
            {
                questionKey: 'glic2027_q_cancel_tickets',
                answerKeys: ['glic2027_a_cancel_tickets']
            }
        ]
    },
    {
        titleKey: 'glic2027_section_others',
        items: [
            {
                questionKey: 'glic2027_q_event_schedule',
                answerKeys: ['glic2027_a_event_schedule_1', 'glic2027_a_event_schedule_2'],
                htmlAnswers: true
            },
            {
                questionKey: 'glic2027_q_ticket_for_sessions',
                answerKeys: ['glic2027_a_ticket_for_sessions']
            },
            {
                questionKey: 'glic2027_q_transportation',
                answerKeys: ['glic2027_a_transportation']
            }
        ]
    }
]

const FAQ = () => {
    const { translate } = useTranslate()
    const [expanded, setExpanded] = useState<string[]>([])
    const handleExpand = (panel: string) => {
        setExpanded(prev => {
            if (prev.includes(panel)) {
                return prev.filter(item => item !== panel)
            }
            return [...prev, panel]
        })
    }

    return (
        <Stack bgcolor={'secondary.main'} p={'48px 10px'} gap={3}>
            <Typography variant="h1" color="primary.main" textTransform={'uppercase'} textAlign={'center'}>
                {translate('faq') || 'FAQ'}
            </Typography>
            <Stack gap={3}>
                {FAQItems.map((section: FAQSection) => {
                    const sectionTitle = translate(section.titleKey) || section.titleKey
                    return (
                        <Accordion
                            key={section.titleKey}
                            elevation={0}
                            className="faq-parent"
                            sx={{
                                '.MuiAccordionSummary-root': {}
                            }}>
                            <AccordionSummary expandIcon={<img src={ArrowDownIcon} alt="arrow-down" />}>
                                <Typography variant="h2" color="#000" fontWeight={500} textTransform={'uppercase'}>
                                    {sectionTitle}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ gap: '20px', flexDirection: 'column', display: 'flex' }}>
                                {section.items.map((item: FAQItem) => {
                                    const question = translate(item.questionKey) || item.questionKey
                                    return (
                                        <Accordion
                                            key={item.questionKey}
                                            elevation={0}
                                            className="faq-child"
                                            expanded={expanded.includes(item.questionKey)}
                                            onChange={() => handleExpand(item.questionKey)}>
                                            <AccordionSummary
                                                expandIcon={
                                                    <img src={expanded.includes(item.questionKey) ? UnexpandedIcon : ExpandPlusIcon} alt="arrow-down" />
                                                }>
                                                <Typography variant="h4" color="secondary.dark" lineHeight={'30px'}>
                                                    {question}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                {item.answerKeys.map((answerKey: string) => {
                                                    const answer = translate(answerKey) || answerKey
                                                    if (item.htmlAnswers) {
                                                        return (
                                                            <Typography
                                                                key={answerKey}
                                                                variant="body1"
                                                                color="primary.main"
                                                                dangerouslySetInnerHTML={{ __html: answer }}
                                                            />
                                                        )
                                                    } else {
                                                        return (
                                                            <Typography key={answerKey} variant="body1" color="primary.main">
                                                                {answer}
                                                            </Typography>
                                                        )
                                                    }
                                                })}
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                })}
                            </AccordionDetails>
                        </Accordion>
                    )
                })}
            </Stack>
        </Stack>
    )
}

export default FAQ
